import { useResponsive } from 'front-commons/hooks';
import { useEffect, useRef } from 'react';

/**
 * Publishes the alternative-navigation banner height for Header positioning.
 *
 * ---
 * 🧠 **Context**
 *
 * - Measures the banner on mount and when {@link useResponsive} changes the medium breakpoint.
 * - Removes --simulated-banner-height during effect cleanup.
 *
 * ---
 * @example
 * ```tsx
 * function SimulatedBanner() {
 *   const bannerRef = useBannerHeight();
 *   return <div ref={bannerRef}>Visão simulada: Farmácia Centro</div>;
 * }
 * ```
 *
 * ---
 * @function useBannerHeight
 * @returns {React.RefObject<HTMLDivElement>} Ref to attach to the measured banner.
 */
export function useBannerHeight() {
	const { medium } = useResponsive();

	const bannerRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (bannerRef.current) {
			const bannerHeight = bannerRef.current.getBoundingClientRect().height;

			document.documentElement.style.setProperty('--simulated-banner-height', `${bannerHeight}px`);
		}

		return () => {
			document.documentElement.style.removeProperty('--simulated-banner-height');
		};
	}, [medium]);

	return bannerRef;
}
